// Editor delle COLLEZIONI del negozio (vetrina avanzata): raccolte tematiche di creazioni.
// Lo stato vive in ShopCustomizer e si salva con il pulsante unico dell'editor.
import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowUp, Check, ChevronDown, Eye, EyeOff, Image as ImageIcon, Layers, Plus, Search, Star, Trash2, X,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { optimizeImageUrl } from '../../utils/imageUrl';
import { MAX_COLLECTION_PRODUCTS, MAX_COLLECTIONS } from '../../lib/shopThemes';

const thumb = (p) => {
  if (!p) return null;
  const i = p.customization_options?.main_image_index || 0;
  return optimizeImageUrl(p.images?.[i] || p.images?.[0], 160);
};

const move = (list, index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export default function ShopCollectionsEditor({ collections, onChange, products, menuEnabled }) {
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState('');
  const byId = Object.fromEntries(products.map((p) => [p.id, p]));

  const update = (id, patch) => onChange(collections.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const add = () => {
    if (collections.length >= MAX_COLLECTIONS) {
      toast.error(`Puoi creare al massimo ${MAX_COLLECTIONS} collezioni`);
      return;
    }
    const id = `new-${Date.now()}`;
    onChange([...collections, { id, title: '', description: '', product_ids: [], cover_product_id: null, visible: true }]);
    setOpenId(id);
    setQuery('');
  };

  const remove = (c) => {
    if (!window.confirm(`Eliminare la collezione «${c.title || 'senza nome'}»? Le creazioni restano nel catalogo.`)) return;
    onChange(collections.filter((x) => x.id !== c.id));
  };

  const toggleProduct = (c, pid) => {
    const has = c.product_ids.includes(pid);
    if (!has && c.product_ids.length >= MAX_COLLECTION_PRODUCTS) {
      toast.error(`Al massimo ${MAX_COLLECTION_PRODUCTS} creazioni per collezione`);
      return;
    }
    const ids = has ? c.product_ids.filter((x) => x !== pid) : [...c.product_ids, pid];
    update(c.id, { product_ids: ids, cover_product_id: ids.includes(c.cover_product_id) ? c.cover_product_id : null });
  };

  return (
    <Card className="p-6 space-y-4" data-testid="shop-collections-editor">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-800 flex items-center gap-2"><Layers className="w-4 h-4 text-[#008080]" /> Collezioni</p>
          <p className="text-sm text-slate-500 mt-0.5">
            Raccogli le creazioni per tema, stagione o materiale (es. «Natale in tavola», «Linea Terra»).
            Nel negozio hanno una pagina ciascuna e compaiono in home.
          </p>
        </div>
        <Button type="button" size="sm" onClick={add} disabled={collections.length >= MAX_COLLECTIONS} className="bg-[#008080] hover:bg-[#006666] text-white shrink-0" data-testid="shop-collection-add">
          <Plus className="w-4 h-4 mr-1" /> Nuova collezione
        </Button>
      </div>

      {!menuEnabled && collections.length > 0 && (
        <p className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
          La voce «Collezioni» è disattivata nella scheda Menu: le collezioni non sono visibili ai clienti.
        </p>
      )}

      {collections.length === 0 && (
        <div className="rounded-xl border border-dashed p-8 text-center">
          <Layers className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-2 text-sm text-slate-600">Nessuna collezione ancora.</p>
          <p className="text-xs text-slate-400">Una collezione ben fatta aiuta i clienti a orientarsi e a comprare più di un pezzo.</p>
        </div>
      )}

      <ul className="space-y-3">
        {collections.map((c, index) => {
          const open = openId === c.id;
          const items = c.product_ids.map((id) => byId[id]).filter(Boolean);
          const cover = byId[c.cover_product_id] || items[0];
          const q = query.trim().toLowerCase();
          const candidates = products.filter((p) => !q || p.title.toLowerCase().includes(q));
          return (
            <li key={c.id} className={`rounded-xl border ${open ? 'border-[#008080] shadow-sm' : 'border-slate-200'}`} data-testid="shop-collection-item">
              <div className="p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {thumb(cover) ? <img src={thumb(cover)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
                </div>
                <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setOpenId(open ? null : c.id)}>
                  <p className={`font-medium truncate ${c.title ? 'text-slate-800' : 'text-slate-400 italic'}`}>{c.title || 'Senza nome'}</p>
                  <p className="text-xs text-slate-500">
                    {items.length} {items.length === 1 ? 'creazione' : 'creazioni'}
                    {c.visible === false && ' · nascosta'}
                    {items.length === 0 && c.visible !== false && ' · vuota, non compare nel negozio'}
                  </p>
                </button>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => update(c.id, { visible: c.visible === false })} aria-label={c.visible === false ? 'Mostra' : 'Nascondi'} title={c.visible === false ? 'Mostra ai clienti' : 'Nascondi ai clienti'}>
                    {c.visible === false ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === 0} onClick={() => onChange(move(collections, index, -1))} aria-label="Sposta su"><ArrowUp className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === collections.length - 1} onClick={() => onChange(move(collections, index, 1))} aria-label="Sposta giù"><ArrowDown className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpenId(open ? null : c.id)} aria-label="Modifica">
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>

              {open && (
                <div className="border-t p-4 space-y-4" data-testid="shop-collection-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`col-title-${c.id}`}>Nome</Label>
                      <input
                        id={`col-title-${c.id}`}
                        value={c.title}
                        maxLength={60}
                        onChange={(e) => update(c.id, { title: e.target.value })}
                        placeholder="Es. Natale in tavola"
                        className="mt-1 w-full h-10 px-3 rounded-md border text-sm"
                        data-testid="shop-collection-title"
                      />
                    </div>
                    <div className="sm:row-span-2">
                      <Label htmlFor={`col-desc-${c.id}`}>Descrizione (facoltativa)</Label>
                      <textarea
                        id={`col-desc-${c.id}`}
                        value={c.description}
                        maxLength={400}
                        rows={4}
                        onChange={(e) => update(c.id, { description: e.target.value })}
                        placeholder="Racconta l'idea della collezione: ispirazione, materiali, occasioni"
                        className="mt-1 w-full px-3 py-2 rounded-md border text-sm"
                      />
                    </div>
                  </div>

                  {/* Creazioni scelte, nell'ordine di visualizzazione */}
                  <div>
                    <Label>Creazioni nella collezione ({items.length}/{MAX_COLLECTION_PRODUCTS})</Label>
                    {items.length === 0 ? (
                      <p className="mt-1 text-xs text-slate-500">Scegline qui sotto. La prima (o quella con la stella) fa da copertina.</p>
                    ) : (
                      <ul className="mt-2 space-y-1.5" data-testid="shop-collection-selected">
                        {items.map((p, i) => {
                          const isCover = (c.cover_product_id || items[0]?.id) === p.id;
                          return (
                            <li key={p.id} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
                              <img src={thumb(p)} alt="" className="w-8 h-8 rounded object-cover bg-slate-200" />
                              <span className="flex-1 min-w-0 truncate text-sm">{p.title}</span>
                              <button type="button" onClick={() => update(c.id, { cover_product_id: p.id })} title="Usa come copertina" className={`p-1 rounded ${isCover ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}>
                                <Star className="w-4 h-4" fill={isCover ? 'currentColor' : 'none'} />
                              </button>
                              <button type="button" disabled={i === 0} onClick={() => update(c.id, { product_ids: move(c.product_ids.filter((id) => byId[id]), i, -1) })} className="p-1 text-slate-500 disabled:opacity-30" aria-label="Sposta su"><ArrowUp className="w-3.5 h-3.5" /></button>
                              <button type="button" disabled={i === items.length - 1} onClick={() => update(c.id, { product_ids: move(c.product_ids.filter((id) => byId[id]), i, 1) })} className="p-1 text-slate-500 disabled:opacity-30" aria-label="Sposta giù"><ArrowDown className="w-3.5 h-3.5" /></button>
                              <button type="button" onClick={() => toggleProduct(c, p.id)} className="p-1 text-slate-400 hover:text-red-600" aria-label="Togli"><X className="w-4 h-4" /></button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {/* Scelta delle creazioni */}
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <Label>Aggiungi creazioni</Label>
                      <label className="relative w-48">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca" className="w-full h-8 pl-8 pr-2 rounded-md border text-sm" />
                      </label>
                    </div>
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="shop-collection-picker">
                      {candidates.map((p) => {
                        const selected = c.product_ids.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => toggleProduct(c, p.id)}
                            className={`relative text-left rounded-lg border-2 overflow-hidden ${selected ? 'border-[#008080]' : 'border-transparent hover:border-slate-300'}`}
                            aria-pressed={selected}
                          >
                            <div className="aspect-square bg-slate-100">{thumb(p) && <img src={thumb(p)} alt="" className="w-full h-full object-cover" />}</div>
                            <p className="px-1.5 py-1 text-[11px] leading-tight text-slate-700 line-clamp-2">{p.title}</p>
                            {selected && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#008080] text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>}
                          </button>
                        );
                      })}
                      {candidates.length === 0 && <p className="col-span-full text-sm text-slate-400 py-4 text-center">Nessuna creazione trovata</p>}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t">
                    <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(c)}>
                      <Trash2 className="w-4 h-4 mr-1" /> Elimina collezione
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setOpenId(null)}>Fatto</Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {collections.length > 0 && (
        <p className="text-xs text-slate-500">Le modifiche diventano visibili quando salvi o pubblichi la vetrina. Le creazioni non attive non compaiono nel negozio.</p>
      )}
    </Card>
  );
}
